import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'

/**
 * Individual badge counts
 */
interface BadgeCounts {
    share: number // Pending requests + forgotten events
    request: number // Status changes to outgoing requests
    messages: number // Unread messages
}

/**
 * Options for useBiblionautasBadges hook
 */
interface UseBiblionautasBadgesOptions {
    polling?: boolean
    pollingInterval?: number
    autoFetch?: boolean
}

/**
 * Return type for useBiblionautasBadges hook
 */
interface UseBiblionautasBadgesReturn {
    counts: BadgeCounts
    totalBadge: number
    isLoading: boolean
    fetchCounts: () => Promise<void>
    updateLastSeen: (column: LastSeenColumn) => Promise<void>
}

/**
 * Valid column names for last_seen timestamps
 */
type LastSeenColumn = 'last_seen_shares_at' | 'last_seen_requests_at' | 'last_seen_messages_at'

/**
 * Custom hook for managing Biblionautas badge counts
 * 
 * Centralizes badge logic to count the number of unseen notifications
 */
export function useBiblionautasBadges(
    userId: string | null | undefined,
    options: UseBiblionautasBadgesOptions = {}
): UseBiblionautasBadgesReturn {
    const { polling = false, pollingInterval = 30000, autoFetch = true } = options

    const [counts, setCounts] = useState<BadgeCounts>({
        share: 0,
        request: 0,
        messages: 0,
    })
    const [isLoading, setIsLoading] = useState(false)

    /**
     * Fetch badge counts from database
     */
    const fetchCounts = useCallback(async () => {
        if (!userId) {
            setCounts({ share: 0, request: 0, messages: 0 })
            return
        }

        try {
            setIsLoading(true)

            // 1. Get last_seen timestamps from settings
            const { data: settings } = await supabase
                .from('shared_library_settings')
                .select('last_seen_shares_at, last_seen_requests_at, last_seen_messages_at')
                .eq('user_id', userId)
                .single()

            const lastSeenShares = settings?.last_seen_shares_at || '1970-01-01T00:00:00Z'
            const lastSeenRequests = settings?.last_seen_requests_at || '1970-01-01T00:00:00Z'
            const lastSeenMessages = settings?.last_seen_messages_at || '1970-01-01T00:00:00Z'

            // 2. Count incoming pending requests (Share tab)
            const { count: shareCount } = await supabase
                .from('library_shares')
                .select('*', { count: 'exact', head: true })
                .eq('owner_id', userId)
                .eq('status', 'pending')
                .gt('created_at', lastSeenShares)

            // 3. Count forgotten events (Share tab)
            const { count: forgottenCount } = await supabase
                .from('library_share_history')
                .select('*', { count: 'exact', head: true })
                .eq('owner_id', userId)
                .eq('action', 'forgotten')
                .gt('created_at', lastSeenShares)

            // 4. Count outgoing request changes (Request tab)
            const { count: changesCount } = await supabase
                .from('library_share_history')
                .select('*', { count: 'exact', head: true })
                .eq('biblionaut_id', userId)
                .in('action', ['granted', 'accepted', 'rejected', 'removed'])
                .gt('created_at', lastSeenRequests)

            // 5. Count unread messages (Messages tab)
            const { count: messagesCount } = await supabase
                .from('messages')
                .select('*', { count: 'exact', head: true })
                .neq('sender_id', userId)
                .gt('created_at', lastSeenMessages)

            setCounts({
                share: (shareCount || 0) + (forgottenCount || 0),
                request: changesCount || 0,
                messages: messagesCount || 0,
            })
        } catch (error) {
            console.error('Error fetching badge counts:', error)
            setCounts({ share: 0, request: 0, messages: 0 })
        } finally {
            setIsLoading(false)
        }
    }, [userId])

    /**
     * Update last_seen timestamp for a specific column
     */
    const updateLastSeen = useCallback(
        async (column: LastSeenColumn) => {
            if (!userId) return

            try {
                const timestamp = new Date().toISOString()

                // Use upsert to insert or update in a single operation
                const { error } = await supabase.from('shared_library_settings').upsert(
                    {
                        user_id: userId,
                        [column]: timestamp,
                    },
                    {
                        onConflict: 'user_id',
                        ignoreDuplicates: false, // Ensure update happens
                    }
                )

                if (error) throw error
            } catch (error) {
                console.error('Error updating last seen:', error)
            }
        },
        [userId]
    )

    // Auto-fetch on mount if enabled
    useEffect(() => {
        if (autoFetch) {
            fetchCounts()
        }
    }, [autoFetch, fetchCounts])

    // Setup polling if enabled
    useEffect(() => {
        if (!polling || !userId) return

        const interval = setInterval(fetchCounts, pollingInterval)
        return () => clearInterval(interval)
    }, [polling, pollingInterval, userId, fetchCounts])

    // Calculate total badge count
    const totalBadge = counts.share + counts.request + counts.messages

    return {
        counts,
        totalBadge,
        isLoading,
        fetchCounts,
        updateLastSeen,
    }
}
